// What the front page's drawings share (02-DESIGN/03-console/03-the-signed-out-site.md
// § the visual): a seeded random, so every drawing is the same on every
// visit; the brand's grey and teal read off the console's tokens as numbers a
// canvas can take an alpha with; and the person's reduced-motion preference.

/** mulberry32 is a small seeded random in [0, 1): the same seed, the same drawing. */
export function mulberry32(seed: number): () => number {
  let a = seed;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export type Vec3 = readonly [number, number, number];

export function dist3(a: Vec3, b: Vec3): number {
  return Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
}

/** easeOut is a cubic ease, clamped to [0, 1]. */
export function easeOut(t: number): number {
  const c = Math.max(0, Math.min(1, t));
  return 1 - Math.pow(1 - c, 3);
}

/** rgbOf turns a token's `#rrggbb` into `r, g b` for an `rgba(…)`, or returns the fallback. */
export function rgbOf(hex: string, fallback: string): string {
  const m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex.trim());
  if (m === null) {
    return fallback;
  }
  return [m[1], m[2], m[3]].map((h) => parseInt(h ?? "0", 16)).join(", ");
}

/** Ink is the drawing's two colours: the brand's grey (--ink-3) and the teal (--teal). */
export interface Ink {
  readonly mesh: string;
  readonly teal: string;
}

/** inkOf reads the two colours off the tokens in force at an element. */
export function inkOf(el: Element): Ink {
  const style = getComputedStyle(el);
  return {
    mesh: rgbOf(style.getPropertyValue("--ink-3"), "126, 135, 148"),
    teal: rgbOf(style.getPropertyValue("--teal"), "31, 168, 142"),
  };
}

export const REDUCED_MOTION = "(prefers-reduced-motion: reduce)";

/** reducedMotion is whether the person prefers reduced motion. */
export function reducedMotion(): boolean {
  return typeof window.matchMedia === "function" && window.matchMedia(REDUCED_MOTION).matches;
}

/** fit sizes a canvas's backing store to its box and the screen's density, and returns the box. */
export function fit(canvas: HTMLCanvasElement): { w: number; h: number; dpr: number } {
  const r = canvas.getBoundingClientRect();
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  const w = Math.max(1, Math.round(r.width));
  const h = Math.max(1, Math.round(r.height));
  canvas.width = Math.round(w * dpr);
  canvas.height = Math.round(h * dpr);
  return { w, h, dpr };
}

/** at is `xs[i]` for an index the drawing built itself; one out of range is a defect. */
export function at<T>(xs: readonly T[], i: number): T {
  const x = xs[i];
  if (x === undefined) {
    throw new RangeError(`no element ${i} of ${xs.length}`);
  }
  return x;
}
